import { useEffect, useState, type ReactNode } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

type Opts = {
  message: ReactNode;
  title?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'primary' | 'danger';
};

type Pending = {
  opts: Opts;
  resolve: (v: boolean) => void;
};

// module-level subscriber pattern（sonner と同じ「Host 1回設置」型）
let currentResolver: ((v: boolean) => void) | null = null;
let listener: ((p: Pending | null) => void) | null = null;

/**
 * window.confirm の Promise 代替。
 *   if (!(await confirm("削除しますか？"))) return;
 * 呼出元 function は async 化が必要。
 */
export function confirm(input: Opts | string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const opts: Opts =
      typeof input === 'string' ? { message: input, variant: 'danger' } : input;
    currentResolver = resolve;
    listener?.({ opts, resolve });
  });
}

/** アプリ root に1度だけ置く Host。 */
export function ConfirmDialogHost() {
  const [pending, setPending] = useState<Pending | null>(null);

  useEffect(() => {
    listener = (p) => setPending(p);
    return () => {
      listener = null;
    };
  }, []);

  const close = (value: boolean) => {
    currentResolver?.(value);
    currentResolver = null;
    setPending(null);
  };

  return (
    <ConfirmDialog
      open={pending !== null}
      onClose={() => close(false)}
      onConfirm={() => close(true)}
      title={pending?.opts.title}
      message={pending?.opts.message ?? ''}
      confirmLabel={pending?.opts.confirmLabel}
      cancelLabel={pending?.opts.cancelLabel}
      variant={pending?.opts.variant ?? 'danger'}
    />
  );
}
